import React, {useContext, useEffect} from "react";
import {Button} from "rsuite";
import CalculationContext from "../context";
import {useCalculationSaveMutation} from "../../../store/api";
import {useSelector} from "react-redux";
import {useField} from "../store/fields-slice";
import {useAutoSave, useDirty, useDoAutoSave, useLoading} from "../store/state-slice";
import {selectAllChoices, useChoiceSettings} from "../store/choices-slice";
import {makeId, selectAllChoicesPrices, selectChoicesPricesColumns} from "../store/choices-prices-slice";
import {selectCalculationSaveParams} from "../store/calculation-save";
import {processFlightOverride} from "../store/flight-override";
import {fromRawAmount} from "../../../money/money-tools";
import {currencies} from "../../../currency/model/currencies.model";

export function SaveButton({header}) {
    const {i18n, flash} = useContext(CalculationContext)
    const isFetching = useLoading()
    const autoSave = useAutoSave()
    const doAutoSave = useDoAutoSave()
    const [save, {isLoading}] = useCalculationSaveMutation()
    const saveParams = useSelector(selectCalculationSaveParams)
    const active = useField("active")
    const currency = useField("currency") ?? currencies.EUR.code
    const dirty = useDirty()
    const choices = useSelector(selectAllChoices)
    const choicesPrices = useSelector(selectAllChoicesPrices)
    const choicesPricesColumns = useSelector(selectChoicesPricesColumns)
    const noteNew = useField('note')
    const {useOverrideForAllPax} = useChoiceSettings()

    useEffect(
        () => {
            if (autoSave && doAutoSave && header && dirty && !isLoading) {
                onClick()
            }
        }, [doAutoSave]
    )

    return (
        <Button color="red" loading={isLoading} onClick={onClick} disabled={isFetching}>
            {i18n.tr("sio.save")}
        </Button>
    )

    function onClick() {
        const dates = new Set
        for (const {date} of saveParams.dates) {
            if (dates.has(date)) {
                flash.warning("Diese Kalkulation enthält doppelte Termine und kann daher nicht gespeichert werden.")
                return
            }
            dates.add(date)
        }

        const params = {
            ...saveParams,
            occupancyChoices: [].concat(saveParams.occupancyChoices),
            note: noteNew,
        }

        for (const [choiceId, groupId, paxId] of choicesPricesColumns) {
            const choiceIndex = choices.findIndex(({id}) => id === choiceId)
            const hasPax = undefined !== paxId && null !== paxId;
            const {
                purchase,
                retail,
            } = hasPax ? choices[choiceIndex].columns.find(c => c.key === groupId + '|' + paxId) :
                choices[choiceIndex].columns.find(({key}) => key.startsWith(groupId))
            const idToFind = makeId(choiceId, groupId, paxId);

            const prices = choicesPrices.find(({id}) => id === idToFind);

            // value is lost during object destruct
            const getPriceByProperty = (property) =>
                prices && 'object' === typeof prices ? prices[property] : undefined;

            const retailPrice = getPriceByProperty('retailPrice');
            const manualFactor = getPriceByProperty('manualFactor');
            const upchargeGroupBasePrice = getPriceByProperty('upchargeGroupBasePrice');
            const subsidyDiscount = getPriceByProperty('subsidyDiscount');
            const memberPrice = getPriceByProperty('memberPrice');

            const price = retailPrice
                ?? getPriceByProperty('retailPriceDefault')
                ?? fromRawAmount(retail, currency).amount;

            // @fixme do better validation in core
            if (
                active &&
                ("number" !== typeof purchase || ("number" !== typeof price && "string" !== typeof price)) &&
                ('00001-00001' !== paxId && !/^SGL/.test(choices[choiceIndex].shortLabel))
            ) {
                flash.warning("Aktive Kalkulation kann nicht gespeichert werden, " +
                    "da es nicht für den kompletten Geltungsbereich Einkaufs- und Verkaufspreise gibt.")
                return;
            }

            const value = {};

            if (undefined !== retailPrice) {
                value.retailPrice = '' === retailPrice ? null : retailPrice;
            } else if (undefined !== manualFactor) {
                value.manualFactor = '' === manualFactor ? null : manualFactor;
            }

            if (undefined !== upchargeGroupBasePrice) {
                value.upchargeGroupBasePrice = '' === upchargeGroupBasePrice ? null : upchargeGroupBasePrice;
            }

            if (undefined !== subsidyDiscount) {
                value.subsidyDiscount = '' === subsidyDiscount ? null : subsidyDiscount;
            } else if (undefined !== memberPrice) {
                value.memberPrice = '' === memberPrice ? null : memberPrice;
            }

            const overrideData = params.occupancyChoices[choiceIndex].overrideData || {};
            const groupOverride = {...(overrideData[groupId] || {})};

            if (!useOverrideForAllPax && undefined !== groupOverride.upchargeGroupBasePrice) {
                delete groupOverride.upchargeGroupBasePrice;
            }

            if (useOverrideForAllPax && undefined !== value.upchargeGroupBasePrice) {
                groupOverride.upchargeGroupBasePrice = value.upchargeGroupBasePrice;
            }

            params.occupancyChoices[choiceIndex] = {
                ...params.occupancyChoices[choiceIndex],
                overrideData: {
                    ...overrideData,
                    [groupId]: hasPax
                        ? {
                            ...groupOverride,
                            [paxId]: {
                                ...(groupOverride[paxId] || {}),
                                ...value,
                            },
                        }
                        : {
                            ...groupOverride,
                            ...value,
                        },
                },
            };
        }

        processFlightOverride(params);
        save(params);
    }
}
